import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
  isDocumentCategorySelected,
} from "../../constants/documentManagementConstants";
import type { DatasetOptionUi, RoleOptionUi } from "../../types/documentManagementUiTypes";
import { findDatasetById } from "../../utils/documentManagementUiUtils";
import { DocumentRoleAccessSelect } from "../shared/DocumentRoleAccessSelect";

export type DocumentUploadOptionsSection = "destination" | "category" | "submit";

export type DocumentUploadOptionsProps = {
  busy: boolean;
  busyLabel?: string;
  apiDatasets: DatasetOptionUi[];
  datasetTargetMode: "existing" | "new";
  onDatasetTargetModeChange: (mode: "existing" | "new") => void;
  datasetSelection: number | "";
  onDatasetSelectionChange: (id: number) => void;
  newDatasetName: string;
  onNewDatasetNameChange: (value: string) => void;
  newDatasetDescription: string;
  onNewDatasetDescriptionChange: (value: string) => void;
  activeDataset?: DatasetOptionUi;
  onRenameDataset: () => void;
  uploadRoleAccess: string[];
  onUploadRoleAccessChange: (roleAccess: string[]) => void;
  roleOptions: RoleOptionUi[];
  uploadCategory: string;
  onUploadCategoryChange: (value: string) => void;
  section: DocumentUploadOptionsSection;
  submitDisabled?: boolean;
  filesCount?: number;
};

function UploadFormField({
  label,
  children,
  sx,
}: {
  label: ReactNode;
  children: ReactNode;
  sx?: object;
}) {
  return (
    <Box className="dm-upload-field" sx={sx}>
      <Typography variant="bodySmall" component="div" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export function DocumentUploadOptions({
  filesCount = 0,
  busy,
  busyLabel,
  apiDatasets,
  datasetTargetMode,
  onDatasetTargetModeChange,
  datasetSelection,
  onDatasetSelectionChange,
  newDatasetName,
  onNewDatasetNameChange,
  newDatasetDescription,
  onNewDatasetDescriptionChange,
  activeDataset,
  onRenameDataset,
  uploadRoleAccess,
  onUploadRoleAccessChange,
  roleOptions,
  uploadCategory,
  onUploadCategoryChange,
  section,
  submitDisabled = true,
}: DocumentUploadOptionsProps) {
  const { t } = useTranslation();
  const categoryMissing = !isDocumentCategorySelected(uploadCategory);
  const roleAccessRaw = JSON.stringify(uploadRoleAccess);
  const rolesInvalid =
    uploadRoleAccess.length > 0 &&
    !uploadRoleAccess.includes("*") &&
    !uploadRoleAccess.some((r) => Boolean(r?.trim()));

  if (section === "submit") {
    return (
      <Button
        type="submit"
        variant="contained"
        disabled={submitDisabled}
        startIcon={
          busy ? <CircularProgress size={18} color="inherit" /> : undefined
        }
        sx={{ textTransform: "none" }}
      >
        {busy
          ? busyLabel ||
            t("opva.dmUploading", { defaultValue: "Uploading…" })
          : filesCount > 1
            ? t("opva.dmUploadN", {
                defaultValue: "Upload {{count}} files",
                count: filesCount,
              })
            : t("opva.dmUpload", { defaultValue: "Upload" })}
      </Button>
    );
  }

  if (section === "category") {
    return (
      <Box className="dm-upload-step-panel">
        <Typography variant="subtitle1" className="dm-upload-step-panel__title">
          {t("opva.dmUploadStepCategoryTitle", {
            defaultValue: "Document category",
          })}
        </Typography>
        <UploadFormField
          label={
            <>
              {t("opva.dmColCategory", { defaultValue: "Category" })}
              <span className="asterisk">*</span>
            </>
          }
          sx={{ mt: 2 }}
        >
          <FormControl
            fullWidth
            variant="standard"
            error={categoryMissing}
            disabled={busy}
          >
            <Select
              variant="standard"
              value={uploadCategory}
              onChange={(e) => onUploadCategoryChange(e.target.value)}
              displayEmpty
              renderValue={(value) =>
                DOCUMENT_CATEGORY_LABELS[
                  (value || "") as keyof typeof DOCUMENT_CATEGORY_LABELS
                ] ??
                t("opva.dmSelectCategory", {
                  defaultValue: "Select category",
                })
              }
            >
              {DOCUMENT_CATEGORIES.map((c) => (
                <MenuItem key={c || "none"} value={c}>
                  {DOCUMENT_CATEGORY_LABELS[c]}
                </MenuItem>
              ))}
            </Select>
            {categoryMissing ? (
              <FormHelperText>
                {t("opva.dmUploadCategoryRequired", {
                  defaultValue: "Choose a category to enable upload.",
                })}
              </FormHelperText>
            ) : null}
          </FormControl>
        </UploadFormField>
      </Box>
    );
  }

  return (
    <Box className="dm-upload-step-panel">
      <Typography variant="subtitle1" className="dm-upload-step-panel__title">
        {t("opva.dmUploadStepDestinationTitle", {
          defaultValue: "Storage & access",
        })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {t("opva.dmUploadStepDestinationLead", {
          defaultValue:
            "Choose where files are stored and which roles can use them in search and chat.",
        })}
      </Typography>

      <RadioGroup
        row
        value={datasetTargetMode}
        onChange={(_, v) => onDatasetTargetModeChange(v as "existing" | "new")}
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          value="existing"
          control={<Radio size="small" />}
          disabled={!apiDatasets.length}
          label={t("opva.dmDatasetExisting", {
            defaultValue: "Existing dataset",
          })}
        />
        <FormControlLabel
          value="new"
          control={<Radio size="small" />}
          label={t("opva.dmDatasetNew", {
            defaultValue: "New dataset",
          })}
        />
      </RadioGroup>

      {datasetTargetMode === "existing" ? (
        apiDatasets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("opva.dmNoDatasetsUseNew", {
              defaultValue:
                "No datasets yet. Switch to “New dataset” and enter a name — it is created on upload.",
            })}
          </Typography>
        ) : (
          <UploadFormField
            label={t("opva.dmColDataset", { defaultValue: "Dataset" })}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "flex-end" }}
            >
              <FormControl fullWidth variant="standard" disabled={busy}>
                <Select
                  variant="standard"
                  value={datasetSelection === "" ? "" : datasetSelection}
                  onChange={(e) =>
                    onDatasetSelectionChange(Number(e.target.value))
                  }
                  displayEmpty
                  renderValue={() => {
                    if (datasetSelection === "") {
                      return t("opva.dmSelectDataset", {
                        defaultValue: "Select a dataset",
                      });
                    }
                    const ds = findDatasetById(datasetSelection, apiDatasets);
                    return ds?.name ?? String(datasetSelection);
                  }}
                >
                  {apiDatasets.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      <ListItemText primary={d.name} secondary={d.description} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                type="button"
                variant="outlined"
                size="small"
                disabled={busy || !activeDataset}
                onClick={onRenameDataset}
                sx={{
                  flexShrink: 0,
                  textTransform: "none",
                  minWidth: { sm: "5.5rem" },
                  whiteSpace: "nowrap",
                  mb: { xs: 0, sm: 0.25 },
                }}
              >
                {t("opva.dmRenameDataset", { defaultValue: "Rename" })}
              </Button>
            </Stack>
          </UploadFormField>
        )
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t("opva.dmNewDatasetHint", {
              defaultValue:
                "Created when you upload. If the name already exists, files are added to that dataset.",
            })}
          </Typography>
          <UploadFormField
            label={
              <>
                {t("opva.dmNewDatasetName", { defaultValue: "Dataset name" })}
                <span className="asterisk">*</span>
              </>
            }
          >
            <TextField
              value={newDatasetName}
              onChange={(e) => onNewDatasetNameChange(e.target.value)}
              fullWidth
              variant="standard"
              required
              disabled={busy}
            />
          </UploadFormField>
          <UploadFormField
            label={t("opva.dmNewDatasetDescription", {
              defaultValue: "Description (optional)",
            })}
          >
            <TextField
              value={newDatasetDescription}
              onChange={(e) => onNewDatasetDescriptionChange(e.target.value)}
              fullWidth
              variant="standard"
              multiline
              minRows={2}
              disabled={busy}
            />
          </UploadFormField>
        </Stack>
      )}

      <Divider sx={{ my: 2.5 }} />

      <UploadFormField
        label={
          <>
            {t("opva.dmColRoles", { defaultValue: "Roles" })}
            <span className="asterisk">*</span>
          </>
        }
      >
        <DocumentRoleAccessSelect
          roleAccessRaw={roleAccessRaw}
          roleOptions={roleOptions}
          disabled={busy || !roleOptions.length}
          onChange={onUploadRoleAccessChange}
          variant="standard"
        />
        {rolesInvalid ? (
          <FormHelperText error sx={{ mx: 0 }}>
            {t("opva.dmRoleRequired", {
              defaultValue: "Select at least one role or choose all roles.",
            })}
          </FormHelperText>
        ) : null}
      </UploadFormField>
    </Box>
  );
}
